import React from 'react';
import { Badge, Tooltip } from '@drone-js/core';
import { ActivityBadgeCategory, BadgeMargin, BadgeWithTooltipProps } from 'component/common/Badge/BadgeService.types';
import debounce from 'lodash/debounce';
import { InView } from 'react-intersection-observer';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesBadgeHoverEventActivitiesFieldsV4, ActivitiesBadgeSeenEventActivitiesFieldsV4 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesBadgeHoverEventActivitiesManifestV4, ActivitiesBadgeSeenEventActivitiesManifestV4 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ScreenSize } from 'store/slice/screen';
import { BadgeType } from 'gql/autogen/BadgeType';
import LoyaltyBadge, { LoyaltyTier } from 'component/common/Badge/LoyaltyBadge';

interface OwnProps extends BadgeWithTooltipProps {
    activityId: number;
    screenSize: ScreenSize;
    badgeCategory: ActivityBadgeCategory;
    badgeMargin?: BadgeMargin;
    badgeType: BadgeType;
    supplierId?: number;
}

const BadgeWithTooltip: React.FC<OwnProps> = React.memo((props) => {
    const { badge, tooltip, activityId, screenSize, badgeCategory, badgeType } = props;
    const { label, variant, trailingAsset, leadingAsset, labelVariant, ...rest } = badge;

    if (!label || !variant) return null;

    const badgeCategoryString = badgeCategory.toString();
    const onBadgeSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesBadgeSeenEventActivitiesFieldsV4>(
            ActivitiesBadgeSeenEventActivitiesManifestV4.ids,
            {
                activity_id: activityId,
                activity_badge_category: badgeCategoryString,
                action_element_value: label,
                activity_badge_type: badgeType,
            },
            { supplier_id: props.supplierId },
        );
    };

    const onLoyaltyBadgeSeen = (inView: boolean) => {
        if (!inView || !badge.loyaltyTier) return;
        const value = `${badge.loyaltyTier}--${label}`;
        trackElementEvent<ActivitiesBadgeSeenEventActivitiesFieldsV4>(
            ActivitiesBadgeSeenEventActivitiesManifestV4.ids,
            {
                activity_id: activityId,
                activity_badge_category: badgeCategoryString,
                action_element_value: value,
                activity_badge_type: badgeType,
            },
            { supplier_id: props.supplierId },
        );
    };

    const onBadgeHover = debounce(() => {
        trackElementEvent<ActivitiesBadgeHoverEventActivitiesFieldsV4>(
            ActivitiesBadgeHoverEventActivitiesManifestV4.ids,
            {
                activity_id: activityId,
                activity_badge_category: badgeCategoryString,
                action_element_value: label,
                activity_badge_type: badgeType,
            },
            { supplier_id: props.supplierId },
        );

        trackElementEvent<ActivitiesBadgeSeenEventActivitiesFieldsV4>(
            ActivitiesBadgeSeenEventActivitiesManifestV4.ids,
            {
                activity_id: activityId,
                activity_badge_category: badgeCategoryString,
                action_element_value: tooltip?.title || '',
                activity_badge_type: badgeType,
            },
            { supplier_id: props.supplierId },
        );
    }, 200);

    const isWithTooltip = tooltip && tooltip.title && !screenSize.mobile;
    const isLoyaltyBadge = !!badge.loyaltyTier;

    if (isWithTooltip) {
        return (
            <Tooltip usePortal={true} {...tooltip} zIndex={1}>
                <Badge
                    role="presentation"
                    label={label}
                    variant={variant}
                    trailingAsset={trailingAsset}
                    leadingAsset={leadingAsset}
                    labelVariant={labelVariant}
                    onMouseEnter={onBadgeHover}
                    me={props.badgeMargin?.me}
                    ms={props.badgeMargin?.ms}
                    mb={props.badgeMargin?.mb}
                    mt={props.badgeMargin?.mt}
                    maxLines={2}
                    contentAlignment="start"
                    {...rest}
                />
            </Tooltip>
        );
    }
    if (isLoyaltyBadge) {
        return (
            <InView onChange={onLoyaltyBadgeSeen} triggerOnce={true} threshold={1}>
                <LoyaltyBadge role="presentation" label={label} tier={badge.loyaltyTier as LoyaltyTier} />
            </InView>
        );
    }
    return (
        <InView onChange={onBadgeSeen} triggerOnce={true} threshold={1}>
            <Badge
                role="presentation"
                label={label}
                variant={variant}
                trailingAsset={trailingAsset}
                leadingAsset={leadingAsset}
                labelVariant={labelVariant}
                me={props.badgeMargin?.me}
                ms={props.badgeMargin?.ms}
                mb={props.badgeMargin?.mb}
                mt={props.badgeMargin?.mt}
                maxLines={2}
                contentAlignment="start"
                {...rest}
            />
        </InView>
    );
});

BadgeWithTooltip.displayName = 'BadgeWithTooltip';
export default BadgeWithTooltip;
